<template>
  <div class="certificate-media">
    <img
      v-if="shouldShowImage"
      :key="source || undefined"
      ref="imageElement"
      :src="source || undefined"
      :alt="alt"
      :loading="loading"
      decoding="async"
      @load="handleImageLoad"
      @error="handleImageError"
    />
    <div v-else class="certificate-media-fallback" role="img" :aria-label="`${alt}，图片暂不可用`">
      <i class="i-lucide-file-image" aria-hidden="true"></i>
      <span>证书图片暂不可用</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  interface Props {
    alt: string
    loading?: 'eager' | 'lazy'
    source?: null | string
  }

  defineOptions({
    name: 'CertificateMedia',
  })

  const props = withDefaults(defineProps<Props>(), {
    loading: 'lazy',
    source: null,
  })

  const imageElement = useTemplateRef<HTMLImageElement>('imageElement')
  const imageFailed = shallowRef(!props.source)
  const shouldShowImage = computed(() => Boolean(props.source) && !imageFailed.value)
  let imageTimeout: ReturnType<typeof setTimeout> | undefined

  /** 清理远程图片超时检查。 */
  function clearImageTimeout() {
    if (imageTimeout) clearTimeout(imageTimeout)
    imageTimeout = undefined
  }

  /** 图片加载成功后保留接口媒体。 */
  function handleImageLoad() {
    clearImageTimeout()
    imageFailed.value = false
  }

  /** 图片不可用时切换为明确的空媒体状态。 */
  function handleImageError() {
    clearImageTimeout()
    imageFailed.value = true
  }

  /** 兼容 SSR hydration 前已经触发 error 的图片。 */
  function checkHydratedImage() {
    const image = imageElement.value
    if (image?.complete && image.naturalWidth === 0) handleImageError()
  }

  /** 长时间无响应的远程图片不永久占据空白区域。 */
  function scheduleImageTimeout() {
    clearImageTimeout()
    if (!props.source || !/^https?:\/\//.test(props.source)) return

    imageTimeout = setTimeout(() => {
      const image = imageElement.value
      if (!image?.complete || image.naturalWidth === 0) imageFailed.value = true
    }, 8000)
  }

  /** 切换证书时重置媒体状态并重新检查。 */
  function resetImageState() {
    clearImageTimeout()
    imageFailed.value = !props.source
    if (!props.source) return

    void nextTick(() => {
      checkHydratedImage()
      if (!imageFailed.value) scheduleImageTimeout()
    })
  }

  onMounted(resetImageState)
  onUnmounted(clearImageTimeout)
  watch(() => props.source, resetImageState)
</script>

<style scoped lang="scss">
  .certificate-media {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #eef0f2;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .certificate-media-fallback {
    position: absolute;
    inset: 0;
    display: grid;
    gap: 10px;
    place-content: center;
    padding: 24px;
    font-size: 13px;
    color: var(--muted);
    text-align: center;
  }

  .certificate-media-fallback i {
    width: 30px;
    height: 30px;
    margin-inline: auto;
    color: rgb(105 82 85 / 58%);
  }
</style>
